import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import { Grid, TextField, Typography } from "@mui/material";
import { useRecoilValue } from "recoil";
import { userCredentials } from "recoilState/GlobalState";

import { Button } from "components";
import XGrid from "components/UI/Grid/XGrid";
import btnStyles from "components/UI/Button/FormButton.module.scss";
import {
    loadReportePresentismo,
    reportePresentismoTitles,
} from "services/reportePresentismo";

import modalContentStyles from "./ReportePresentismoModal.module.scss";

/** Mismo alto que .gridWrap (px explícitos para el resize observer del DataGrid). */
const GRID_HEIGHT_PX = 480;

export default function ReportePresentismoModal({ usuario, closeModal, open }) {
    const credentials = useRecoilValue(userCredentials);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [fechaDesde, setFechaDesde] = useState(() =>
        moment().subtract(13, "day").format("YYYY-MM-DD")
    );
    const [fechaHasta, setFechaHasta] = useState(() =>
        moment().format("YYYY-MM-DD")
    );
    const [gridSelection, setGridSelection] = useState({ status: false, row: {} });

    const fetchRows = useCallback(
        async (desde, hasta) => {
            if (!usuario?.id || usuario.usuarioId == null) return;
            setLoading(true);
            setError(null);
            try {
                const { rows: data, error: err } = await loadReportePresentismo({
                    credentials,
                    merchantServicioUsuarioId: usuario.id,
                    usuarioId: usuario.usuarioId,
                    fechaDesde: desde,
                    fechaHasta: hasta,
                });
                if (err) {
                    setError(err);
                    setRows([]);
                } else {
                    setRows(data);
                }
            } catch (e) {
                setError(
                    e?.message || "Error al cargar el reporte de presentismo."
                );
                setRows([]);
            } finally {
                setLoading(false);
            }
        },
        [credentials, usuario]
    );

    useEffect(() => {
        if (!open || !usuario?.id || usuario.usuarioId == null) {
            if (!open) setRows([]);
            return;
        }
        const desde = moment().subtract(13, "day").format("YYYY-MM-DD");
        const hasta = moment().format("YYYY-MM-DD");
        setFechaDesde(desde);
        setFechaHasta(hasta);
        fetchRows(desde, hasta);
    }, [open, usuario?.id, usuario?.usuarioId, fetchRows]);

    const handleConsultar = () => {
        if (moment(fechaDesde).isAfter(moment(fechaHasta), "day")) {
            setError("La fecha desde no puede ser posterior a la fecha hasta.");
            return;
        }
        fetchRows(fechaDesde, fechaHasta);
    };

    return (
        <>
            <div className={modalContentStyles.body}>
                <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                    <Grid item>
                        <TextField
                            label="Desde"
                            type="date"
                            size="small"
                            value={fechaDesde}
                            onChange={(e) => setFechaDesde(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item>
                        <TextField
                            label="Hasta"
                            type="date"
                            size="small"
                            value={fechaHasta}
                            onChange={(e) => setFechaHasta(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item>
                        <Button type="button" onClick={handleConsultar} disabled={loading}>
                            Consultar
                        </Button>
                    </Grid>
                </Grid>
                {error && (
                    <Typography color="error" variant="body2" sx={{ mb: 1 }}>
                        {error}
                    </Typography>
                )}
                <div className={modalContentStyles.gridWrap}>
                    <XGrid
                        rows={rows}
                        titles={reportePresentismoTitles}
                        loading={loading}
                        hideFooter={rows.length <= 10}
                        height={GRID_HEIGHT_PX}
                        secondarySelected={gridSelection}
                        setSecondarySelected={setGridSelection}
                    />
                </div>
            </div>
            <div className={"closeButtonContainer"}>
                <Button className={btnStyles.defaultBtn} onClick={closeModal}>
                    Cerrar
                </Button>
            </div>
        </>
    );
}
